import type { CoresSignals } from '@fraudeai/schema';
import { defineModule, type ModuleContext } from '../harness.js';
import { attempt, attemptAsync } from '../util/safe.js';

/**
 * Измерение настоящего числа параллельных потоков.
 *
 * `navigator.hardwareConcurrency` — обычное число, которое антидетект правит
 * одной строкой. Проверить его можно тем, что подделать нельзя: реальной
 * способностью машины считать параллельно.
 *
 * Метод. Пул воркеров получает одинаковое окно времени, и считается, сколько
 * работы вся группа успела выполнить. Пока воркеров не больше, чем ядер, они
 * работают одновременно и суммарная выработка растёт почти линейно. Как
 * только воркеров становится больше, они начинают делить одни и те же ядра,
 * и рост прекращается. Точка перелома и есть число ядер.
 *
 * Почему меряется выработка, а не длительность. Первая версия давала
 * воркерам фиксированный объём работы и замеряла время. Объём подбирался
 * калибровкой, на быстрой машине раздувался до полутора миллионов итераций,
 * и восемь воркеров занимали все ядра целиком. Главный поток при этом
 * голодал, `setTimeout` не получал шанса выполниться — и внешний предел
 * времени, который должен был всё оборвать, не срабатывал. Страница вставала
 * намертво в Safari и в Яндекс.Браузере. Когда длительность задаёт сам
 * воркер, замер укладывается в известное время независимо и от машины, и от
 * того, успевает ли главный поток вообще что-либо делать.
 *
 * Нарочно без SharedArrayBuffer и Atomics. Тот путь известнее, но требует
 * заголовков COOP/COEP на всём сайте, а они запрещают встраивать сторонние
 * ресурсы без их согласия — цена, которой этот сигнал не стоит.
 *
 * Что метод честно не умеет. На занятой машине параллелизм измеряется
 * заниженным. Поэтому результат сопровождается оценкой достоверности, а
 * правило на сервере реагирует только на разрыв в разы.
 */

/**
 * Тело воркера.
 *
 * Считает подряд идущие числа на простоту делением: работа держит АЛУ
 * занятым и не трогает память, поэтому упирается именно в число ядер, а не
 * в пропускную способность шины. Останавливается воркер сам, по времени.
 */
const WORKER_SOURCE = `
self.onmessage = function (event) {
  var deadline = performance.now() + event.data.ms;
  var done = 0;
  var acc = 0;
  while (performance.now() < deadline) {
    for (var n = 2; n < 2000; n++) {
      var prime = 1;
      for (var d = 2; d * d <= n; d++) {
        if (n % d === 0) { prime = 0; break; }
      }
      acc += prime;
    }
    done++;
  }
  self.postMessage(done);
};
`;

/** Сколько потоков проверять — чтобы увидеть и завышение, и занижение.
 *
 * Шестнадцать снова безопасны: работу теперь ограничивает не объём, а время,
 * и каждый прогон длится своё окно независимо от числа воркеров. Предел в
 * восемь, поставленный при починке зависания, оказался вреден — на машинах с
 * бо́льшим числом ядер замер упирался в собственный потолок и любой настоящий
 * браузер выглядел завышающим их число.
 */
const PROBE_LIMIT = 16;

/**
 * Длительность одного прогона.
 *
 * Замер идёт ровно столько и не зависит от быстродействия машины. Шесть
 * прогонов плюс прогрев и контроль укладываются примерно в секунду.
 */
const WINDOW_MS = 120;

interface Pool {
  workers: Worker[];
  dispose: () => void;
}

function createPool(size: number): Pool | null {
  return attempt(() => {
    const blob = new Blob([WORKER_SOURCE], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const workers: Worker[] = [];

    try {
      for (let i = 0; i < size; i++) workers.push(new Worker(url));
    } catch {
      // Часть воркеров создать не удалось — работаем с тем, что получилось.
    }

    if (workers.length === 0) {
      attempt(() => URL.revokeObjectURL(url));
      return null;
    }

    return {
      workers,
      dispose: () => {
        for (const worker of workers) attempt(() => worker.terminate());
        // Адрес освобождается только здесь. Отзывать его сразу после
        // создания нельзя: Safari загружает код воркера асинхронно, и к
        // моменту загрузки адрес оказывается недействителен — воркер не
        // стартует, сообщений не присылает, а страница ждёт его вечно.
        attempt(() => URL.revokeObjectURL(url));
      },
    };
  });
}

/**
 * Запускает `count` воркеров на `ms` миллисекунд и возвращает суммарную
 * выработку. `null` означает, что хотя бы один воркер не ответил и прогон
 * засчитывать нельзя.
 */
async function runBatch(pool: Pool, count: number, ms: number): Promise<number | null> {
  return attemptAsync(async () => {
    const results = await Promise.all(
      pool.workers.slice(0, count).map(
        (worker) =>
          new Promise<number>((resolve) => {
            // Свой предохранитель на случай молчащего воркера. Ноль означает
            // «не сработал», и такой прогон отбрасывается целиком.
            const guard = setTimeout(() => resolve(0), ms * 4 + 1000);
            worker.onmessage = (event: MessageEvent<number>) => {
              clearTimeout(guard);
              resolve(event.data);
            };
            worker.onerror = () => {
              clearTimeout(guard);
              resolve(0);
            };
            worker.postMessage({ ms });
          }),
      ),
    );

    if (results.some((value) => value <= 0)) return null;
    return results.reduce((a, b) => a + b, 0);
  });
}

/**
 * Находит точку насыщения по ряду замеров.
 *
 * Пока есть свободные ядра, суммарная выработка растёт с каждым воркером;
 * после насыщения перестаёт. Насыщением считается последнее число потоков,
 * на котором прирост ещё заметен.
 */
function saturationPoint(work: Map<number, number>): number | null {
  const points = [...work.entries()].sort(([a], [b]) => a - b);
  if (points.length < 2) return null;
  if (points.some(([, done]) => done <= 0)) return null;

  let best = points[0][0];
  let bestWork = points[0][1];

  for (let i = 1; i < points.length; i++) {
    // Порог в 15% отделяет настоящий прирост от шума планировщика.
    if (points[i][1] > bestWork * 1.15) {
      bestWork = points[i][1];
      best = points[i][0];
    }
  }
  return best;
}

export const coresModule = defineModule({
  id: 'cores',
  phase: 2,
  timeoutMs: 6000,
  async run(ctx: ModuleContext): Promise<CoresSignals> {
    const declared = ctx.signals.navigator?.hardwareConcurrency ?? null;

    const unsupported: CoresSignals = {
      supported: false,
      declared,
      measured: null,
      timings: null,
      reliable: false,
      note: 'воркеры недоступны',
    };

    if (typeof Worker === 'undefined' || typeof Blob === 'undefined') return unsupported;

    // На мобильных замер не проводится. Ядра там разной мощности, вывод от
    // этого смазывается, а цена ошибки — подвисший телефон у обычного
    // посетителя. Заявленное число ядер на телефоне подделывают редко.
    if (ctx.signals.navigator?.parsed?.isMobile) {
      return { ...unsupported, note: 'на мобильных не измеряется' };
    }

    const pool = createPool(PROBE_LIMIT);
    if (!pool) return unsupported;

    try {
      // Прогрев: первое обращение к воркеру платит за его запуск и за
      // компиляцию кода, и в измерение этот прогон попадать не должен.
      await runBatch(pool, pool.workers.length, 40);

      const timings: Record<string, number> = {};
      const work = new Map<number, number>();

      const steps = [1, 2, 3, 4, 6, 8, 12, 16].filter((k) => k <= pool.workers.length);
      for (const count of steps) {
        const done = await runBatch(pool, count, WINDOW_MS);
        if (done === null) break;
        work.set(count, done);
        timings[String(count)] = done;
      }

      if (work.size < 2) {
        return { ...unsupported, note: 'слишком мало замеров' };
      }

      // Контрольный прогон одним воркером в конце серии.
      //
      // Прямая проверка того, что машина всё это время была свободна. Если
      // выработка на одном потоке заметно изменилась, значит рядом шла
      // посторонняя нагрузка и ряд снят при разных условиях — сравнивать
      // точки в нём нельзя. Без такой проверки занятая машина выглядела бы
      // малоядерной, и честный пользователь получал бы обвинение в подмене.
      const first = work.get(1) ?? 0;
      const last = await runBatch(pool, 1, WINDOW_MS);
      const steady = last !== null && first > 0 && Math.abs(last - first) < first * 0.4;

      const measured = saturationPoint(work);

      // Замер, упёршийся в собственный потолок, — это нижняя граница, а не
      // значение. Если рост не прекратился на последнем проверенном числе
      // потоков, настоящее их число может быть любым бо́льшим, и сравнивать
      // такую величину с заявленной нельзя.
      const tested = [...work.keys()];
      const atCeiling = measured !== null && measured === Math.max(...tested);

      return {
        supported: true,
        declared,
        measured,
        timings,
        reliable: steady && !atCeiling,
        note: atCeiling ? 'рост не прекратился — величина является нижней границей' : null,
      };
    } finally {
      pool.dispose();
    }
  },
});
